"use client";

import type { StateCreator } from "zustand";
import type { WebPlayerTrackRequest } from "@/hooks/webPlayerTypes";

export type DataBaseline = {
  gsrRawAdc: number | null;
  gsrSmoothAdc: number | null;
  gsrUiLevel: number | null;
};

export type SurveyValues = {
  pain: number;
  energy: number;
  positivity: number;
  baseline: {
    baseline_gsr: number | null;
    baseline_gsr_raw_adc?: number | null;
    baseline_gsr_smooth_adc?: number | null;
    baseline_gsr_ui_level?: number | null;
    baseline_source?: string | null;
  };
};

export type SessionStatus =
  | "idle"
  | "starting"
  | "active"
  | "ending"
  | "completed"
  | "error";

export type SessionMediaControllerProvider = "youtube" | "applemusic";

export type SessionMediaPlayRequest = WebPlayerTrackRequest & {
  provider: SessionMediaControllerProvider;
  queueUris?: string[];
  queueIndex?: number;
  queueSource?: string | null;
};

type SessionMediaController = {
  playTrack: (request: SessionMediaPlayRequest) => void | Promise<void>;
  setShuffle: (enabled: boolean) => void | Promise<void>;
};

type SessionMediaControllers = Record<
  SessionMediaControllerProvider,
  SessionMediaController | null
>;

export type SessionSlice = {
  sessionId: string | null;
  blockedSessionId: string | null;
  sessionActive: boolean;
  status: SessionStatus;
  mediaControllers: SessionMediaControllers;

  // --- simple status controls ---
  setStatus: (status: SessionStatus) => void;
  setBlockedSessionId: (sessionId: string | null) => void;
  clearBlockedSessionId: () => void;
  resetSession: () => void;
  registerMediaController: (
    provider: SessionMediaControllerProvider,
    controller: SessionMediaController | null
  ) => void;
  requestMediaPlay: (request: SessionMediaPlayRequest) => void;
  requestMediaShuffle: (
    provider: SessionMediaControllerProvider,
    enabled: boolean
  ) => void;
};

function createEmptyMediaControllers(): SessionMediaControllers {
  return {
    youtube: null,
    applemusic: null,
  };
}

export const createSessionSlice: StateCreator<
  SessionSlice,
  [],
  [],
  SessionSlice
> = (set, get) => ({
  sessionId: null,
  blockedSessionId: null,
  sessionActive: false,
  status: "idle",
  mediaControllers: createEmptyMediaControllers(),

  setStatus: (status) =>
    set({ status, sessionActive: status === "active" }),

  setBlockedSessionId: (sessionId) => set({ blockedSessionId: sessionId }),
  clearBlockedSessionId: () => set({ blockedSessionId: null }),

  resetSession: () =>
    set({
      sessionId: null,
      blockedSessionId: null,
      sessionActive: false,
      status: "idle",
      mediaControllers: createEmptyMediaControllers(),
    }),
  registerMediaController: (provider, controller) =>
    set((state) => ({
      mediaControllers: {
        ...state.mediaControllers,
        [provider]: controller,
      },
    })),
  requestMediaPlay: (request) => {
    const uri = typeof request.uri === "string" ? request.uri.trim() : "";
    if (!uri) return;
    const controller = get().mediaControllers[request.provider];
    if (!controller) return;
    void Promise.resolve().then(() =>
      controller.playTrack({
        ...request,
        uri,
      })
    );
  },
  requestMediaShuffle: (provider, enabled) => {
    const controller = get().mediaControllers[provider];
    if (!controller) return;
    void Promise.resolve().then(() => controller.setShuffle(Boolean(enabled)));
  },
});
